import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Dialog, InputBase } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { IconSearch } from "@tabler/icons-react";
import tokens from "../../theme/tokens";

const { color, font, radius, shadow } = tokens;

/** Subsequence match ("snhptl" matches "Sneha Patil"), scored by how early it lands. */
const fuzzy = (needle, haystack) => {
  const n = needle.toLowerCase();
  const h = haystack.toLowerCase();
  if (!n) return 0;
  let i = 0;
  let score = 0;
  for (let j = 0; j < h.length && i < n.length; j += 1) {
    if (h[j] === n[i]) {
      score += j === 0 || h[j - 1] === " " ? 2 : 1;
      i += 1;
    }
  }
  return i === n.length ? score : -1;
};

/**
 * ⌘K palette: jump to a job or candidate, or run an action on the current selection.
 * Opened by ⌘K / Ctrl-K anywhere in the console.
 */
const CommandBar = ({ open, onClose, jobs, candidates, actions, jobIdOf }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const listRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery("");
      setCursor(0);
    }
  }, [open]);

  const items = useMemo(() => {
    const all = [
      ...actions.map((a) => ({ ...a, kind: "Action" })),
      ...jobs.map((j) => ({
        kind: "Job",
        label: j["Job Title"] || "Untitled job",
        run: () => navigate(`/dashboard/jobs/${jobIdOf(j)}`),
      })),
      ...candidates.map((c) => ({
        kind: "Candidate",
        label: c.name,
        hint: c.stage,
        run: () => c.open(),
      })),
    ];

    if (!query.trim()) return all.slice(0, 12);

    return all
      .map((item) => ({ item, score: fuzzy(query, item.label) }))
      .filter(({ score }) => score >= 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 12)
      .map(({ item }) => item);
  }, [query, jobs, candidates, actions, navigate, jobIdOf]);

  // Keep the cursor inside the (shrinking) result list as the user types.
  useEffect(() => {
    setCursor((c) => Math.min(c, Math.max(0, items.length - 1)));
  }, [items.length]);

  const run = (item) => {
    onClose();
    item?.run?.();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => (c + 1) % Math.max(1, items.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => (c - 1 + items.length) % Math.max(1, items.length));
    } else if (e.key === "Enter") {
      e.preventDefault();
      run(items[cursor]);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 560,
          maxWidth: "100%",
          borderRadius: `${radius.modal}px`,
          boxShadow: shadow.modal,
          alignSelf: "flex-start",
          mt: 10,
        },
      }}
      BackdropProps={{ sx: { bgcolor: "rgba(23,23,31,.4)" } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 2,
          py: 1.75,
          borderBottom: `1px solid ${color.border}`,
        }}
      >
        <IconSearch size={17} stroke={1.75} color={color.textMuted} />
        <InputBase
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Jump to a job or candidate, or run an action…"
          sx={{
            flex: 1,
            font: `400 13.5px ${font.sans}`,
            "& input::placeholder": { color: color.textMuted, opacity: 1 },
          }}
        />
        <Box
          component="kbd"
          sx={{
            font: `500 10.5px ${font.mono}`,
            border: `1px solid ${color.borderInput}`,
            borderRadius: `${radius.chip}px`,
            px: 0.625,
            color: color.textMuted,
          }}
        >
          esc
        </Box>
      </Box>

      <Box ref={listRef} sx={{ maxHeight: 340, overflowY: "auto", py: 0.75 }}>
        {!items.length && (
          <Box sx={{ px: 2, py: 3, font: `400 12.5px ${font.sans}`, color: color.textMuted }}>
            Nothing matches "{query}".
          </Box>
        )}
        {items.map((item, i) => (
          <Box
            key={`${item.kind}-${item.label}-${i}`}
            onMouseEnter={() => setCursor(i)}
            onClick={() => run(item)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              px: 2,
              py: 1.125,
              cursor: "pointer",
              bgcolor: i === cursor ? color.rowTint : "transparent",
            }}
          >
            <Box
              sx={{
                font: `500 10px ${font.mono}`,
                textTransform: "uppercase",
                color: color.textMuted,
                width: 68,
                flex: "none",
              }}
            >
              {item.kind}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, font: `500 13px ${font.sans}`, color: color.text }}>
              {item.label}
            </Box>
            {item.kbd && (
              <Box
                component="kbd"
                sx={{
                  font: `500 10px ${font.mono}`,
                  border: `1px solid ${color.borderInput}`,
                  borderRadius: `${radius.chip}px`,
                  px: 0.625,
                  color: color.textMuted,
                }}
              >
                {item.kbd}
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Dialog>
  );
};

export default CommandBar;
